/**
 * Client display names by foreign_national_id, fetched once per id and kept
 * for the session, so practice-wide activity rows can say who they are about.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ForeignNationalsAPI } from "../../api/foreignNationals";
import { getFullName } from "../clientProfile/lib/format";

const cache = new Map<string, string>();
const inflight = new Map<string, Promise<void>>();

/** Test hook: forget every cached name. */
export const resetClientNamesForTests = (): void => {
  cache.clear();
  inflight.clear();
};

/** Pre-seed names (e.g. from a dashboard payload that already carries them). */
export const seedClientNames = (
  entries: Iterable<[string, string | null | undefined]>
): void => {
  for (const [id, name] of entries) {
    const label = (name ?? "").trim();
    if (id && label) cache.set(id, label);
  }
};

const fetchName = async (id: string): Promise<void> => {
  try {
    const fn = await ForeignNationalsAPI.get(id);
    cache.set(id, getFullName(fn) || "Client");
  } catch {
    cache.set(id, "Client");
  }
};

export const useClientNames = (): {
  /** The name when known; requests it otherwise and returns null. */
  nameOf: (foreignNationalId: string | null | undefined) => string | null;
  /** Make sure these ids are (being) resolved. */
  ensure: (ids: Iterable<string>) => void;
} => {
  const [, bump] = useState(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const ensure = useCallback((ids: Iterable<string>) => {
    Array.from(ids)
      .map((raw) => String(raw ?? "").trim())
      .filter((id) => id && !cache.has(id) && !inflight.has(id))
      .forEach((id) => {
        const p = fetchName(id).finally(() => {
          inflight.delete(id);
          if (mounted.current) bump((n) => n + 1);
        });
        inflight.set(id, p);
      });
  }, []);

  const nameOf = useCallback(
    (foreignNationalId: string | null | undefined) => {
      const id = String(foreignNationalId ?? "").trim();
      if (!id) return null;
      const hit = cache.get(id);
      if (hit) return hit;
      ensure([id]);
      return null;
    },
    [ensure]
  );

  return { nameOf, ensure };
};
